// globalContextEngine.js
// Phase 0 - Global Context & Displacement

function analyzeTimeframe(candles, lookback = 30) {
  if (!Array.isArray(candles) || candles.length < lookback) {
    return null;
  }

  const recent = candles.slice(-lookback);
  let impulse = 0;
  let totalRange = 0;

  for (let index = 1; index < recent.length; index += 1) {
    impulse += recent[index].close - recent[index - 1].close;
    totalRange += recent[index].high - recent[index].low;
  }

  if (!totalRange) {
    return null;
  }

  return {
    impulse,
    efficiency: Math.abs(impulse) / totalRange,
    direction: impulse > 0 ? "UP" : impulse < 0 ? "DOWN" : "FLAT",
  };
}

function buildNeutralContext(market, reason) {
  return {
    valid: false,
    market,
    globalBias: "NEUTRAL",
    direction: "NEUTRAL",
    strength: 0,
    strengthMax: 1,
    strengthDisplay: "0.00/1.00",
    biasConfidence: 0,
    spikeExpectation: "NONE",
    reason,
  };
}

export function detectGlobalContext(timeframes, market) {
  const { M5, M15, M30 } = timeframes || {};

  if (!M5 || !M15 || !M30) {
    return buildNeutralContext(market, "Missing HTF data");
  }

  const tf5 = analyzeTimeframe(M5);
  const tf15 = analyzeTimeframe(M15);
  const tf30 = analyzeTimeframe(M30);

  if (!tf5 || !tf15 || !tf30) {
    return buildNeutralContext(market, "Insufficient HTF candles");
  }

  const directions = [tf5.direction, tf15.direction, tf30.direction];
  const upVotes = directions.filter((direction) => direction === "UP").length;
  const downVotes = directions.filter((direction) => direction === "DOWN").length;
  const dominantVotes = Math.max(upVotes, downVotes);
  const averageEfficiency =
    (tf5.efficiency + tf15.efficiency + tf30.efficiency) / 3;
  const biasConfidence =
    dominantVotes === 0
      ? 0
      : ((dominantVotes / directions.length) + averageEfficiency) / 2;

  let globalBias = "NEUTRAL";
  let direction = "NEUTRAL";

  if (upVotes > downVotes && upVotes >= 2) {
    globalBias = "UP_PRESSURE";
    direction = "BULLISH";
  }

  if (downVotes > upVotes && downVotes >= 2) {
    globalBias = "DOWN_PRESSURE";
    direction = "BEARISH";
  }

  let spikeExpectation = "NONE";

  if (market === "BOOM" && direction === "BULLISH") {
    spikeExpectation = "UP_SPIKE_FAVORED";
  } else if (market === "CRASH" && direction === "BEARISH") {
    spikeExpectation = "DOWN_SPIKE_FAVORED";
  } else if (
    (market === "BOOM" && direction === "BEARISH") ||
    (market === "CRASH" && direction === "BULLISH")
  ) {
    spikeExpectation = "HTF_CONFLICT";
  }

  return {
    valid: true,
    market,
    globalBias,
    direction,
    strength: Number(averageEfficiency.toFixed(2)),
    strengthMax: 1,
    strengthDisplay: `${averageEfficiency.toFixed(2)}/1.00`,
    biasConfidence: Number(biasConfidence.toFixed(2)),
    spikeExpectation,
    reason: "Confirmed M5, M15, and M30 impulse alignment",
  };
}
